import { makeAutoObservable, action, computed } from "mobx";
import { GraphStore } from "./GraphStore";
import { RangeStore } from "./RangeStore";
import { ZoomStore } from "./ZoomStore";

export type ChartType = "line" | "area" | "column" | "spline" | "areaspline";

export interface ChartStoreConfig {
	structureId: Dryp.StructureId;
	defaultType?: ChartType;
	height?: number;
}

export class ChartStore {
	structureId: Dryp.StructureId;
	chartType: ChartType = "line";
	graphList: Map<string, GraphStore> = new Map();
	graphOrder: string[] = [];
	rangeList: Map<string, RangeStore> = new Map();
	private lastFrom: string = "";
	private lastTo: string = "";

	readonly zoom: ZoomStore;

	readonly height: number;

	constructor(config: ChartStoreConfig) {
		this.structureId = config.structureId;
		this.chartType = config.defaultType ?? "line";
		this.height = config.height ?? 200;
		this.zoom = new ZoomStore(range => {
			// Sync zoom range to all graphs for re-downsampling
			this.graphs.forEach(graph => graph.setZoomRange(range.min, range.max));
		});

		makeAutoObservable(this, {
			setChartType: action,
			addGraph: action,
			removeGraph: action,
			moveGraphUp: action,
			moveGraphDown: action,
			addRange: action,
			removeRange: action,
			clear: action,
			fetchAll: action,
			refetch: action,
			syncZoomToGraphs: action,
			loading: computed,
			loaded: computed,
			hasError: computed,
			hasData: computed,
			ranges: computed,
			visibleRanges: computed,
		});
	}

	setChartType(type: ChartType) {
		this.chartType = type;
	}

	addGraph(parameter: Dryp.Parameter): GraphStore {
		const key = String(parameter.id);
		if (this.graphList.has(key)) {
			return this.graphList.get(key)!;
		}

		const graph = new GraphStore(this.structureId, parameter, this.graphOrder.length);
		this.graphList.set(key, graph);
		if (!this.graphOrder.includes(key)) {
			this.graphOrder.push(key);
		}
		return graph;
	}

	removeGraph(id: string) {
		this.graphList.delete(id);
		this.graphOrder = this.graphOrder.filter(graphId => graphId !== id);
		this.reorderGraphs();
	}

	getGraph(id: string): GraphStore | undefined {
		return this.graphList.get(id);
	}

	addRange(key: string, name: string, color: string): RangeStore {
		if (this.rangeList.has(key)) {
			return this.rangeList.get(key)!;
		}
		const range = new RangeStore(name, color);
		this.rangeList.set(key, range);
		return range;
	}

	getRange(key: string): RangeStore | undefined {
		return this.rangeList.get(key);
	}

	removeRange(key: string) {
		this.rangeList.delete(key);
	}

	moveGraphUp(id: string) {
		const index = this.graphOrder.indexOf(id);
		if (index > 0) {
			[this.graphOrder[index - 1], this.graphOrder[index]] = [this.graphOrder[index], this.graphOrder[index - 1]];
			this.reorderGraphs();
		}
	}

	moveGraphDown(id: string) {
		const index = this.graphOrder.indexOf(id);
		if (index < this.graphOrder.length - 1) {
			[this.graphOrder[index], this.graphOrder[index + 1]] = [this.graphOrder[index + 1], this.graphOrder[index]];
			this.reorderGraphs();
		}
	}

	private reorderGraphs() {
		this.graphOrder.forEach((id, index) => {
			const graph = this.graphList.get(id);
			if (graph) {
				graph.setOrder(index);
			}
		});
	}

	async fetchAll(from: string, to: string) {
		this.lastFrom = from;
		this.lastTo = to;
		const promises = this.graphs.map(graph => graph.fetch(from, to));
		await Promise.all(promises);
	}

	async refetch() {
		if (!this.lastFrom || !this.lastTo) return;
		// Clear cache for these graphs and refetch
		this.graphs.forEach(graph => graph.clearCache(this.lastFrom, this.lastTo));
		await this.fetchAll(this.lastFrom, this.lastTo);
	}

	/** Sync current zoom range to all graphs for re-downsampling */
	syncZoomToGraphs() {
		const { min, max } = this.zoom.range;
		this.graphs.forEach(graph => graph.setZoomRange(min, max));
	}

	clear() {
		this.graphList.clear();
		this.graphOrder = [];
		this.rangeList.clear();
	}

	get graphs(): GraphStore[] {
		const unique = [...new Set(this.graphOrder)];
		return unique.map(id => this.graphList.get(id)!).filter(Boolean);
	}

	get visibleGraphs(): GraphStore[] {
		return this.graphs.filter(graph => graph.visible);
	}

	get ranges(): RangeStore[] {
		return [...this.rangeList.values()];
	}

	get visibleRanges(): RangeStore[] {
		return this.ranges.filter(range => range.visible);
	}

	get chartHeight(): number {
		return this.height;
	}

	get loading(): boolean {
		return this.graphs.some(graph => graph.loading);
	}

	get loaded(): boolean {
		return this.graphs.length > 0 && this.graphs.every(graph => graph.loaded);
	}

	get hasError(): boolean {
		return this.graphs.some(graph => graph.error !== null);
	}

	get hasData(): boolean {
		return this.graphs.some(graph => graph.hasData);
	}

	get isNotConfigured(): boolean {
		return this.graphs.length > 0 && this.graphs.every(graph => graph.isNotConfigured);
	}

	get notConfiguredGraphs(): GraphStore[] {
		return this.graphs.filter(graph => graph.isNotConfigured);
	}
}
